import {
  createOperationalFailure,
  type FailureClassification,
  type FailureContext,
  type OperationalFailure,
} from "../failures.js";

export type LlmProtocol = "anthropic" | "openai-compatible" | "google-gemini";

export type LlmTaskId =
  | "direct.episode-titles"
  | "direct.batch-extract"
  | "direct.episode-synopsis"
  | "direct.synopsis-reduction"
  | "direct.script-synopsis"
  | "direct.asset-grouping"
  | "direct.asset-curation"
  | "direct.asset-curation-audit"
  | "direct.location-canonicalization"
  | "direct.state-curation"
  | "direct.state-binding"
  | "metadata.global"
  | "metadata.descriptions"
  | "synopsis.chunk"
  | "synopsis.episode"
  | "synopsis.arc"
  | "synopsis.script-overview"
  | "ingest.body-start"
  | "ingest.arc-summary"
  | "ingest.text-normalize"
  | "ingest.episode-assets"
  | "ingest.name-fix"
  | "ingest.mention-resolve"
  | "ingest.asset-extract"
  | "ingest.asset-curate"
  | "ingest.asset-recall"
  | "ingest.asset-refine"
  | "ingest.state-reconcile"
  | "ingest.asset-audit"
  | "ingest.speaker-audit"
  | "ingest.video-serial";

export type LlmTaskMode = "text" | "tool" | "video";
export type JsonSchema = Record<string, unknown>;

export interface TextGenerationRequest {
  system: string;
  user: string;
  maxOutputTokens: number;
  responseFormat?: "json_object";
  signal?: AbortSignal;
}

export interface ToolDefinition {
  name: string;
  description: string;
  inputSchema: JsonSchema;
}

export interface ToolGenerationRequest extends TextGenerationRequest {
  tool: ToolDefinition;
}

// Token counts extracted from the provider response. All optional: a provider (or
// proxy) that doesn't return usage leaves these undefined, and the accounting treats
// undefined as zero — never as an error.
export interface TokenUsage {
  inputTokens?: number; // total input (already includes the two cache buckets below)
  outputTokens?: number;
  cachedInputTokens?: number; // cache-read portion — a subset of inputTokens, billed cheap (~0.1x)
  cacheCreationTokens?: number; // cache-write portion — a subset of inputTokens, billed ~1.25x
}

export interface TextGenerationResponse {
  text: string;
  finishReason: string;
  attempts: number;
  usage?: TokenUsage;
  raw?: unknown;
}

export interface ToolGenerationResponse {
  input: unknown;
  finishReason: string;
  attempts: number;
  usage?: TokenUsage;
  raw?: unknown;
}

export interface LlmAdapter {
  generateText(request: TextGenerationRequest): Promise<TextGenerationResponse>;
  generateTool(request: ToolGenerationRequest): Promise<ToolGenerationResponse>;
}

// Video understanding (§10.5). A handle to an uploaded media file (Gemini Files
// API): the remote resource is uploaded once and reused across passes. The URI
// is ephemeral — it never enters manifests or fingerprints.
export interface VideoFileHandle {
  name: string; // Files API resource name, e.g. "files/abc-123"
  uri: string; // file URI for a fileData part
  mimeType: string;
}

export type VideoUploadMode = "file-api" | "base64";
export const MAX_INLINE_VIDEO_BYTES = 100_000_000;

export type VideoMediaInput =
  | { kind: "file-api"; file: VideoFileHandle }
  | { kind: "base64"; data: string; mimeType: string }
  | { kind: "local"; path: string; mimeType: string }
  | {
      kind: "local-fallback";
      path: string;
      mimeType: string;
      primaryFailure: OperationalFailure;
    };

export type VideoProviderCredential =
  | { kind: "literal"; value: string }
  | { kind: "env"; name: string };

export interface VideoProviderOverrides {
  provider?: "gemini" | "doubao";
  baseUrl?: string;
  apiKey?: VideoProviderCredential;
  model?: string;
}

export interface LlmExecutorOptions {
  videoProvider?: VideoProviderOverrides;
}

export interface VideoGenerationRequest {
  media: VideoMediaInput;
  system?: string;
  user: string;
  fps: number;
  maxOutputTokens: number;
  responseJsonSchema?: JsonSchema; // structured output (corrections)
  // Path to the local (transcoded) media on disk. The primary Gemini path ignores
  // this — it uses the already-uploaded `file` handle. A cross-provider fallback
  // (Doubao/Ark) can't reuse that handle, so it re-uploads this local file to its
  // own Files API. Present whenever the caller still holds the local file.
  localPath?: string;
  localMime?: string;
  signal?: AbortSignal;
}

export interface VideoGenerationResponse {
  text: string;
  finishReason: string;
  attempts: number;
  usage?: TokenUsage;
  raw?: unknown;
}

// The Gemini adapter implements this port; it owns prepare/generate/release and
// does not import ingest domain or construct business prompts (§10.5, §13).
export interface VideoUnderstandingPort {
  prepare(localPath: string, mimeType: string, mode: VideoUploadMode, signal?: AbortSignal): Promise<VideoMediaInput>;
  generate(request: VideoGenerationRequest): Promise<VideoGenerationResponse>;
  release(media: VideoMediaInput, signal?: AbortSignal): Promise<void>;
}

// A task-bound view of the video port: the executor injects the task's output
// budget so callers omit maxOutputTokens (mirrors generateText(task, req)).
export interface TaskVideoPort {
  prepare(localPath: string, mimeType: string, mode: VideoUploadMode, signal?: AbortSignal): Promise<VideoMediaInput>;
  generate(request: Omit<VideoGenerationRequest, "maxOutputTokens">): Promise<VideoGenerationResponse>;
  release(media: VideoMediaInput, signal?: AbortSignal): Promise<void>;
}

export interface LlmRouteInfo {
  task: LlmTaskId;
  profile: string;
  provider: string;
  protocol: LlmProtocol;
  baseUrl: string | null;
  model: string;
  fingerprint: string;
}

export interface LlmRouteUsage extends LlmRouteInfo {
  calls: number;
  succeeded: number;
  failed: number;
  reused: number;
  attempts: number;
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheCreationTokens: number;
}

export type LlmUsageStatus = "succeeded" | "failed" | "reused";

// One immutable accounting event per routed model execution. Unlike `usage()`,
// which is a convenience aggregate, this is the lossless source used by ingest's
// streaming usage ledger. Fallback models carry their actual provider/model route.
export interface LlmUsageEvent extends LlmRouteInfo {
  sequence: number;
  status: LlmUsageStatus;
  attempts: number;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  inputTokens: number;
  outputTokens: number;
  cachedInputTokens: number;
  cacheCreationTokens: number;
  errorCode?: LlmErrorCode;
}

export interface LlmTaskExecutor {
  generateText(task: LlmTaskId, request: Omit<TextGenerationRequest, "maxOutputTokens">): Promise<TextGenerationResponse>;
  generateTool(task: LlmTaskId, request: Omit<ToolGenerationRequest, "maxOutputTokens">): Promise<ToolGenerationResponse>;
  videoPort(task: LlmTaskId): TaskVideoPort;
  route(task: LlmTaskId): LlmRouteInfo;
  routes(): LlmRouteInfo[];
  recordReuse(task: LlmTaskId): void;
  usage(): LlmRouteUsage[];
  usageEvents(): LlmUsageEvent[];
  readonly configSource: "builtin" | "file";
}

export type LlmErrorCode =
  | "auth"
  | "permission_denied"
  | "endpoint"
  | "network"
  | "timeout"
  | "rate_limited"
  | "request_failed"
  | "truncated"
  | "content_filtered"
  | "empty_response"
  | "invalid_response"
  | "cancelled";

function defaultFailureClassification(code: LlmErrorCode): FailureClassification {
  switch (code) {
    case "auth": return { failure_type: "authentication", failure_code: "authentication_failed" };
    case "permission_denied": return { failure_type: "authorization", failure_code: "permission_denied" };
    case "endpoint": return { failure_type: "provider", failure_code: "resource_not_found" };
    case "network": return { failure_type: "network", failure_code: "network_failed" };
    case "timeout": return { failure_type: "timeout", failure_code: "request_timeout" };
    case "rate_limited": return { failure_type: "rate_limit", failure_code: "rate_limited" };
    case "request_failed": return { failure_type: "provider", failure_code: "request_rejected" };
    case "truncated": return { failure_type: "response", failure_code: "output_truncated" };
    case "content_filtered": return { failure_type: "policy", failure_code: "content_blocked" };
    case "empty_response": return { failure_type: "response", failure_code: "empty_response" };
    case "invalid_response": return { failure_type: "response", failure_code: "malformed_response" };
    case "cancelled": return { failure_type: "cancelled", failure_code: "cancelled" };
  }
}

export interface LlmTransportErrorOptions {
  retryable?: boolean;
  details?: string[];
  attempts?: number;
  usage?: TokenUsage;
  classification?: FailureClassification;
  context?: FailureContext;
  modelFailures?: OperationalFailure[];
}

export class LlmTransportError extends Error {
  readonly code: LlmErrorCode;
  readonly retryable: boolean;
  readonly details: string[];
  readonly failure: OperationalFailure;
  readonly modelFailures: OperationalFailure[];
  attempts: number;
  usage: TokenUsage;

  constructor(code: LlmErrorCode, message: string, options: LlmTransportErrorOptions = {}) {
    super(message);
    this.name = "LlmTransportError";
    this.code = code;
    this.retryable = options.retryable ?? false;
    this.details = options.details ?? [];
    this.attempts = options.attempts ?? 1;
    this.usage = options.usage ?? {};
    this.failure = createOperationalFailure(
      options.classification ?? defaultFailureClassification(code),
      {
        retryable: this.retryable,
        message,
        ...options.context,
        ...(this.details.length > 0 ? { details: this.details } : {}),
      },
    );
    this.modelFailures = options.modelFailures ?? [];
  }
}
